/*
 * SYBAScan analytics — V2 (GA4 funnel via GTM)
 *
 * Pushes custom events to window.dataLayer where the GTM container
 * `GTM-WB578SV` ("Sybotanica B.V. → www.sybotanica.com - 2024") picks them
 * up and forwards to the GA4 property `Sybotanica - GA4 - Elevar`
 * (measurement ID G-HHV6SRY7TB, property 318334523). Configure once in
 * GTM (one Custom-Event trigger matching `^sybscan_`, one GA4 Event tag).
 *
 * Events emitted (every event auto-gets the `sybscan_` prefix here, so
 * call sites pass the suffix only):
 *   session_start        — first SYBAScan page load in a browser session
 *   search_performed     — settled debounced search query (any result count)
 *   search_no_result     — settled with zero results (subset of search_performed)
 *   search_result_clicked — user clicked a search result
 *   search_abandoned     — typed 2+ chars, had results, never clicked, page hidden
 *   scan_started         — image upload begins
 *   scan_recognized      — Kindwise returned a usable match
 *   scan_no_result       — Kindwise failed (with `reason` param)
 *   scan_rejected        — implicit thumbs-down (user clicked "try again" after
 *                          a recognised result)
 *
 * GA4 funnel uses session_start → (search_performed|scan_started) →
 * (search_result_clicked|scan_recognized) → view_item → add_to_cart → purchase.
 * `view_item` / `add_to_cart` / `purchase` are fired by Elevar's standard
 * ecommerce dataLayer pipeline already — we don't touch those.
 *
 * Why we push to window.dataLayer and not ElevarDataLayer:
 *   The Elevar dashboard is admin-gated and we don't have access to
 *   register custom events there. window.dataLayer + GTM-WB578SV puts the
 *   routing under our own admin control. Same end-destination (GA4).
 *
 * Session attribution:
 *   sybscan_sid (UUID, 365-day localStorage) is stamped on every event.
 *   Lets us correlate same-user activity across the funnel. GA4's own
 *   client_id sessionization joins our events to standard `purchase` events
 *   automatically — we don't need to do anything else for conversion attribution.
 */
(function () {
  'use strict';

  var SID_KEY = 'sybscan_sid';
  var SID_TTL_MS = 365 * 24 * 60 * 60 * 1000;
  var SESSION_START_KEY = 'sybscan_session_start_fired';

  function uuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function getSid() {
    try {
      var raw = localStorage.getItem(SID_KEY);
      if (raw) {
        var parsed = JSON.parse(raw);
        if (parsed && parsed.id && parsed.created && Date.now() - parsed.created < SID_TTL_MS) {
          return parsed.id;
        }
      }
    } catch (e) {}
    var fresh = { id: uuid(), created: Date.now() };
    try {
      localStorage.setItem(SID_KEY, JSON.stringify(fresh));
    } catch (e) {}
    return fresh.id;
  }

  // Consent: defaults to allow. First-party functional telemetry, no third-party
  // domain, no PII. Pandectes can override by setting
  // window.Pandectes.consent.analytics === false. A host page can also assign
  // window.SybScanAnalytics.consentGranted to its own check.
  function defaultConsent() {
    try {
      if (
        window.Pandectes &&
        window.Pandectes.consent &&
        window.Pandectes.consent.analytics === false
      ) {
        return false;
      }
    } catch (e) {}
    return true;
  }

  // Push to GTM-WB578SV's dataLayer. Flat properties (GA4 + GTM both prefer flat
  // event_params over nested objects). Event name auto-prefixed with `sybscan_`
  // so GTM's regex trigger `^sybscan_` matches all our events in one rule.
  function pushDataLayer(suffix, properties) {
    var name = 'sybscan_' + suffix;
    var ctx = window.SybScan || {};
    var payload = {
      event: name,
      sybscan_sid: getSid(),
      sybscan_locale: ctx.locale || '',
      sybscan_country: ctx.country || '',
      sybscan_page_url: location.href,
      sybscan_referrer: document.referrer || '',
      sybscan_ts: Date.now()
    };
    if (properties && typeof properties === 'object') {
      for (var k in properties) {
        if (Object.prototype.hasOwnProperty.call(properties, k)) {
          // Flatten — direct copy. GTM's Data Layer Variables read top-level keys.
          payload[k] = properties[k];
        }
      }
    }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) {}
  }

  function send(suffix, properties) {
    if (!api.consentGranted()) return;
    pushDataLayer(suffix, properties);
  }

  // Fire session_start once per browser session (tab session). sessionStorage
  // resets on tab close which matches our intent — a customer who returns
  // tomorrow gets a fresh session_start, GA4 will also have rolled their
  // session. session_start is the funnel-anchor stage.
  function fireSessionStartOnce() {
    try {
      if (sessionStorage.getItem(SESSION_START_KEY) === '1') return;
      sessionStorage.setItem(SESSION_START_KEY, '1');
    } catch (e) {
      // sessionStorage blocked (private browsing edge case) — fire anyway,
      // GA4 will dedupe on session_id if it gets fired twice.
    }
    send('session_start', {});
  }

  // Pending-search state, flushed as 'search_abandoned' on pagehide /
  // visibility-change-to-hidden if the user never selected a result.
  // Cleared on selection or new query.
  var pending = null;

  function setPendingAbandon(state) {
    pending = state
      ? {
          query: state.query || '',
          resultCount: typeof state.resultCount === 'number' ? state.resultCount : null,
          ts: Date.now()
        }
      : null;
  }

  function clearPendingAbandon() {
    pending = null;
  }

  function flushPendingAbandon() {
    if (!pending) return;
    var snapshot = pending;
    pending = null;
    send('search_abandoned', {
      query: snapshot.query,
      result_count: snapshot.resultCount,
      dwelled_ms: Date.now() - snapshot.ts
    });
  }

  window.addEventListener('pagehide', flushPendingAbandon);
  window.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flushPendingAbandon();
  });

  var api = {
    getSid: getSid,
    consentGranted: defaultConsent,
    // track() takes a suffix WITHOUT the `sybscan_` prefix — the prefix is
    // added centrally so the GTM trigger regex stays simple. Call sites
    // emit short names like `search_performed`, `scan_started`, etc.
    track: send,
    setPendingSearchAbandon: setPendingAbandon,
    clearPendingSearchAbandon: clearPendingAbandon
  };

  window.SybScanAnalytics = api;

  // Anchor the funnel. Fired once per tab session. The Pandectes consent
  // gate inside send() still applies — if analytics consent is denied,
  // no event leaves the page.
  fireSessionStartOnce();
})();
